// src/pages/CompareResultPage.tsx
import React, { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getComparisonByCode, payResultByCode, generateComparison } from '../api/results';
import type { PublicComparisonState, WalletOnePaymentResponse } from '../api/types';
import { comparisonPurposeLabel } from '../api/comparisonPurposes';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import Footer from '../components/common/Footer';
import { useLang } from '../context/LangContext';
import { downloadBlob } from '../utils/download';

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.background};
`;

const Main = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const Box = styled(Card)`
  width: 100%;
  max-width: 680px;
`;

const Row = styled.p`
  color: ${({ theme }) => theme.colors.textLight};
  margin: 10px 0;
`;

const Notice = styled.p<{ $kind?: 'error' | 'info' | 'success' }>`
  color: ${({ $kind, theme }) =>
    $kind === 'error' ? '#e32636'
    : $kind === 'success' ? '#44944a'
    : theme.colors.info};
  margin: 12px 0;
`;

const Generating = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${({ theme }) => theme.colors.textLight};
  margin: 12px 0;
  font-size: 0.95rem;
`;

const PulseArrow = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 1.3rem;
  animation: pulseArrow 1.2s ease-in-out infinite;

  @keyframes pulseArrow {
    0%, 100% { opacity: 0.35; transform: translateX(0); }
    50% { opacity: 1; transform: translateX(4px); }
  }
`;

const formatElapsed = (s: number): string => {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

const CompareResultPage: React.FC = () => {
  const { code = '' } = useParams<{ code: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLang();
  const [data, setData] = useState<PublicComparisonState | null>(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [downloadCount, setDownloadCount] = useState(0);
  const [now, setNow] = useState(Date.now());
  const processedRef = useRef(false);

  const load = useCallback(async (silent = false) => {
    if (!code) return;
    if (!silent) setLoading(true);
    setError('');
    try {
      const state = await getComparisonByCode(code);
      setData(state);
      setDownloadCount(state.conclusion_public_downloads ?? 0);
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      setError(status === 404 ? t('compareNotFound') : t('compareLoadError'));
    } finally {
      setLoading(false);
    }
  }, [code, t]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const payment = new URLSearchParams(location.search).get('payment');
    if (payment === 'success') setNotice(t('resultPaymentSuccess'));
    if (payment === 'fail') setNotice(t('resultPaymentFail'));
    if (payment === 'success' || payment === 'fail') void load();
  }, [location.search, load, t]);

  // Поллинг, пока идёт генерация (обновление каждые 15 с, без F5).
  useEffect(() => {
    if (data?.status !== 'processing') return;
    const timer = window.setInterval(() => { void load(true); }, 15000);
    return () => window.clearInterval(timer);
  }, [data?.status, load]);

  useEffect(() => {
    if (data?.status !== 'processing') return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [data?.status]);

  // Запуск генерации один раз при открытии (в т.ч. по прямой ссылке):
  // если PDF ещё не готов — ставим задачу (эндпоинт идемпотентен).
  useEffect(() => {
    if (!code || processedRef.current) return;
    processedRef.current = true;
    void (async () => {
      try {
        const state = await getComparisonByCode(code);
        if (!state.pdf_ready) {
          await generateComparison(code);
          await load(true);
        }
      } catch {
        // ошибка уже отобразится в load()
      }
    })();
  }, [code, load]);

  const submitPayment = (payment: WalletOnePaymentResponse) => {
    if (payment.confirmation_url) {
      // ЮKassa: открываем виджет подтверждения в новой вкладке.
      setNotice(t('resultPaymentRedirect'));
      window.open(payment.confirmation_url, '_blank', 'noopener,noreferrer');
      return;
    }
    if (!payment.action) {
      setError(t('resultPaymentUnavailable'));
      return;
    }
    if (!payment.fields || Object.keys(payment.fields).length === 0) {
      setNotice(t('resultPaymentRedirect'));
      window.open(payment.action, '_blank', 'noopener,noreferrer');
      return;
    }
    const form = document.createElement('form');
    form.method = payment.method || 'POST';
    form.action = payment.action;
    form.target = '_blank';
    form.setAttribute('aria-label', t('resultPaymentFormLabel'));
    form.style.position = 'absolute';
    form.style.left = '-10000px';
    Object.entries(payment.fields).forEach(([name, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    });
    document.body.appendChild(form);
    setNotice(t('resultPaymentRedirect'));
    window.setTimeout(() => form.submit(), 0);
  };

  const handlePay = async (event: FormEvent) => {
    event.preventDefault();
    setPaying(true);
    setError('');
    setNotice('');
    try {
      const payment = await payResultByCode(code);
      // TEMP (тест): при PAYMENT_TEST_MOCK заказ уже помечен оплаченным, но
      // внешнюю страницу (Сбер) всё равно открываем в НОВОЙ вкладке.
      if (payment.status === 'paid') {
        submitPayment(payment);
        await load();
      } else {
        submitPayment(payment);
      }
    } catch (paymentError) {
      const status = (paymentError as { response?: { status?: number } })?.response?.status;
      setError(status === 503 ? t('resultPaymentNotConfigured') : t('resultPaymentError'));
    } finally {
      setPaying(false);
    }
  };

  const handleDownload = async () => {
    setError('');
    try {
      const response = await fetch(`/api/compare/by-code/${encodeURIComponent(code)}/pdf?t=${Date.now()}`);
      if (response.status === 403) {
        let detail = '';
        try { detail = (await response.json())?.detail ?? ''; } catch { /* noop */ }
        throw new Error(detail === 'download_limit_reached'
          ? t('resultDownloadLimitReached').replace('{times}', String(data?.download_limit ?? 2))
          : t('resultDownloadRequiresPayment'));
      }
      if (!response.ok) throw new Error(t('resultPdfDownloadError'));
      const blob = await response.blob();
      downloadBlob(blob, `comparison-${code}.pdf`);
      await load(true);
    } catch (downloadError) {
      setError(downloadError instanceof Error ? downloadError.message : t('resultPdfDownloadError'));
    }
  };

  const effectivePaid = data?.paid || data?.allow_unpaid;
  const left = Math.max(0, (data?.download_limit ?? 2) - (data?.conclusion_public_downloads ?? 0));
  // Заголовок: «Психопрофили КОД1 и КОД2» — берём коды участников; если их нет,
  // падаем на общий заголовок сравнения.
  const participantCodes = (data?.participants ?? [])
    .map((p) => p?.share_code)
    .filter((c): c is string => Boolean(c));
  const compareHeading = participantCodes.length >= 2
    ? `Психопрофили ${participantCodes[0]} и ${participantCodes[1]}`
    : (data?.title || t('compareResultTitle'));
  const genStartedAt = data?.generation_started_at ? new Date(data.generation_started_at).getTime() : null;
  const genElapsed = genStartedAt ? Math.max(0, Math.floor((now - genStartedAt) / 1000)) : 0;
  const price = data?.payment_amount ? String(parseFloat(data.payment_amount)) : '—';
  const currency = data?.payment_currency || t('resultCurrency');

  let statusText = data ? t(`resultStatus_${data.status}` as 'resultStatus_processing') : '';
  if (data?.generation_status === 'error' || data?.generation_status === 'blocked') {
    statusText = t('resultStatusRejected');
  }
  if (effectivePaid && data?.pdf_ready && left > 0) {
    statusText = left === 1
      ? t('resultStatusPaidAvailableOne')
      : t('resultStatusPaidAvailableFew').replace('{n}', String(left));
  } else if (effectivePaid && left <= 0) {
    statusText = t('resultStatusLimitReached');
  }

  // «Возврат к предыдущему окну»: если пользователь пришёл с какой-либо страницы
  // (например, /results-gates) — вернуть его на неё; при открытии по прямой ссылке
  // (истории нет) — на страницу ввода кодов сравнения.
  const goBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/compare-entry');
    }
  };

  return (
    <Page>
      <Main>
      <Box>
        <h1>{compareHeading}</h1>
        <Row><strong>{t('resultCodeLabel')}:</strong> {data?.share_code || code}</Row>
        {loading && <Row>{t('genLoading')}</Row>}
        {error && <Notice $kind="error">{error}</Notice>}
        {notice && <Notice>{notice}</Notice>}
        {!loading && data && (
          <>
            <Row><strong>{t('resultStatusLabel')}:</strong> {statusText}</Row>
            <Row><strong>{t('compareResultPrice')}:</strong> {price} {currency}</Row>
            {data?.participants?.map((p, i) => (
              <Row key={i}>
                {p?.share_code || '—'}
                {p?.name ? ` (${p.name})` : ''}
              </Row>
            ))}
            {data?.purpose_code && <Row><strong>{t('comparePurposeLabel')}:</strong> {comparisonPurposeLabel(data.purpose_code)}{data?.purpose_description ? ` — ${data.purpose_description}` : ''}</Row>}
            {data.generation_status === 'processing' && (
              <>
                <Generating>
                  <PulseArrow>⇄</PulseArrow>
                  <span>{t('resultGeneratingInfo').replace('{s}', formatElapsed(genElapsed))}</span>
                </Generating>
                {(data.queue_position ?? 0) > 0 && (
                  <Notice $kind="info">{t('resultQueuedInfo').replace('{n}', String(data.queue_position ?? 0))}</Notice>
                )}
                <Notice $kind="info">{t('compareProcessingInfo')}</Notice>
              </>
            )}
            {data.generation_status === 'error' && (
              <Notice $kind="error">{data.generation_error || t('resultGenerationServiceError')}</Notice>
            )}
            {data.pdf_ready && !effectivePaid && (
              <form onSubmit={handlePay} style={{ marginTop: 20 }}>
                <Button type="submit" variant="primary" size="lg" disabled={paying} fullWidth>
                  {paying ? t('comparePaymentLoading') : t('comparePayment')}
                </Button>
              </form>
            )}
            {data.pdf_ready && effectivePaid && left > 0 && (
              <p style={{ marginTop: 20, textAlign: 'center' }}>
                <Button variant="outline" size="lg" onClick={handleDownload}>
                  {t('compareDownload').replace('{n}', String(left))}
                </Button>
              </p>
            )}
            {data.pdf_ready && effectivePaid && left <= 0 && (
              <Notice $kind="info">{t('resultDownloadLimitReached').replace('{times}', String(data.download_limit ?? 2))}</Notice>
            )}
            {!data.pdf_ready && (
              <p style={{ marginTop: 20, textAlign: 'center' }}>
                <Button variant="outline" size="lg" disabled>
                  {t('compareDownloadPlaceholder')}
                </Button>
              </p>
            )}
          </>
        )}
        <p style={{ marginTop: 20, textAlign: 'center' }}>
          {(!data || data.generation_status !== 'processing') && (
            <Button variant="text" size="md" onClick={goBack}>
              {t('compareResultBack')}
            </Button>
          )}
        </p>
      </Box>
      </Main>
      <Footer />
    </Page>
  );
};

export default CompareResultPage;
